"use client";

import {
  Pencil,
  Trash2,
  Eye,
  Lock,
  MoreVertical,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ROLES } from "@/lib/constants/roles";
import { GLOBAL_FEATURES, VENUE_FEATURES } from "@/lib/constants/features";

export interface User {
  id: number;
  name: string | null;
  email?: string;
  user_id: string;
  role_id: number;
  venue_id: number | null;
  status_id: number;
  roles?: { name: string };
  venues?: { name: string };
  statuses?: { name: string };
  user_venue_permissions?: Array<{
    venue_id: number;
    features?: string[];
    venues?: { id: number; name: string };
  }>;
}

/**
 * The features a user holds at any of their venues: super admins everything,
 * booking staff what is saved on their venues, other roles none (fixed).
 */
function heldFeatures(user: User): "all" | "fixed" | Set<string> {
  if (user.role_id === ROLES.SUPER_ADMIN) return "all";
  if (user.role_id !== ROLES.BOOKING_STAFF) return "fixed";
  return new Set<string>(
    (user.user_venue_permissions ?? []).flatMap((p) => p.features ?? []),
  );
}

const ALL_FEATURES = [...VENUE_FEATURES, ...GLOBAL_FEATURES];

/** Pills shown in the row; the rest are behind "+N". */
const VISIBLE_FEATURE_PILLS = 2;

type Feature = (typeof ALL_FEATURES)[number];

/** One colour per feature, so the same feature reads the same in every row. */
const FEATURE_PILL_COLORS: Record<Feature["key"], string> = {
  bookings: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  customers: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  shows:
    "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  studios:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  kits: "bg-zinc-100 text-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300",
  addons: "bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300",
  shop_items:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  holidays: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  faqs: "bg-stone-100 text-stone-800 dark:bg-stone-900/40 dark:text-stone-300",
  pricing:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  rule_sets:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  price_modifiers:
    "bg-yellow-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
  invoices:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  accounting_settings:
    "bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-300",
  inbox: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  reviews:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  review_questions:
    "bg-yellow-50 text-yellow-900 dark:bg-yellow-950/40 dark:text-yellow-200",
  users:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  email_templates:
    "bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-200",
  venue_settings:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  partners:
    "bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-300",
  venues_admin:
    "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
  ai_settings:
    "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-300",
  inbox_global:
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300",
};

const FeaturePill = ({ feature }: { feature: Feature }) => (
  <span
    title={feature.hint}
    className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${FEATURE_PILL_COLORS[feature.key]}`}
  >
    {feature.label}
  </span>
);

/** "+N" pill listing the remaining features on hover or keyboard focus. */
function MoreFeatures({ features }: { features: Feature[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted/70"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label={`${features.length} more features`}
      >
        +{features.length}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="w-auto max-w-xs p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <div className="flex flex-wrap gap-1">
          {features.map((feature) => (
            <FeaturePill key={feature.key} feature={feature} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface UsersTableProps {
  users: User[];
  /** Hides "Edit user" when left out. */
  onEditUser?: (user: User) => void;
  onDeleteUser: (user: User) => void;
  selectedUsers?: Set<number>;
  onSelectUser?: (userId: number, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  /** Opens a user's permissions or details; defaults to the super admin page. */
  onOpenUser?: (user: User) => void;
  /** Whether a row offers opening the user; every row by default. */
  canOpenUser?: (user: User) => boolean;
  /** Why a row's actions are unavailable, or null; rows are open by default. */
  lockedReason?: (user: User) => string | null;
  /** Label of the delete action, e.g. "Remove from venue". */
  deleteLabel?: string;
}

export default function UsersTable({
  users,
  onEditUser,
  onDeleteUser,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onOpenUser,
  canOpenUser = () => true,
  lockedReason,
  deleteLabel = "Delete",
}: UsersTableProps) {
  const router = useRouter();
  const openUser =
    onOpenUser ?? ((user: User) => router.push(`/superadmin/users/${user.id}`));

  // Handle empty state
  if (!users || users.length === 0) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No users found
          </h3>
          <p className="text-gray-500">
            No users match your current filters. Try adjusting your search
            criteria.
          </p>
        </div>
      </div>
    );
  }

  const getVenueDisplay = (user: User) => {
    // For customers (role_id = 4), check primary venue_id first
    if (user.role_id === 4) {
      if (user.venues?.name) {
        return user.venues.name;
      }
      return "-";
    }

    // For staff users, check user_venue_permissions first
    if (user.user_venue_permissions && user.user_venue_permissions.length > 0) {
      const venueNames = user.user_venue_permissions
        .map((p) => p.venues?.name)
        .filter(Boolean) as string[];

      if (venueNames.length === 0) {
        // If no venue names from permissions, check primary venue
        if (user.venues?.name) {
          return user.venues.name;
        }
        return "-";
      }
      if (venueNames.length === 1) return venueNames[0];

      // Multiple venues - show first venue with count and tooltip
      const allVenuesText = venueNames.join(", ");

      return (
        <span
          className="inline-flex items-center gap-1 cursor-default"
          title={`Venues: ${allVenuesText}`}
        >
          <span>{venueNames[0]}</span>
          <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
            +{venueNames.length - 1}
          </span>
        </span>
      );
    } else if (user.venues?.name) {
      // Fallback to primary venue for any user
      return user.venues.name;
    }

    return "-";
  };

  const renderFeatureCell = (user: User) => {
    const access = heldFeatures(user);
    // Roles with fixed rights (bar staff, hosts, customers) have no features.
    if (access === "fixed") return <td className="p-4" />;
    const held =
      access === "all" ? [] : ALL_FEATURES.filter((f) => access.has(f.key));
    return (
      <td className="p-4">
        <div className="flex max-w-[13rem] flex-wrap items-center gap-1">
          {access === "all" ? (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              All features
            </span>
          ) : held.length === 0 ? (
            <span className="text-xs text-muted-foreground">None</span>
          ) : (
            <>
              {held.slice(0, VISIBLE_FEATURE_PILLS).map((feature) => (
                <FeaturePill key={feature.key} feature={feature} />
              ))}
              {held.length > VISIBLE_FEATURE_PILLS && (
                <MoreFeatures features={held.slice(VISIBLE_FEATURE_PILLS)} />
              )}
            </>
          )}
        </div>
      </td>
    );
  };

  const allSelected =
    selectedUsers &&
    users.length > 0 &&
    users.every((user) => selectedUsers.has(user.id));
  const someSelected = selectedUsers && selectedUsers.size > 0 && !allSelected;

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted">
          <tr>
            {onSelectUser && onSelectAll && (
              <th className="w-12 p-4">
                <Checkbox
                  checked={allSelected}
                  ref={(el) => {
                    // Find the actual input element within the checkbox component
                    const input = el?.querySelector(
                      'input[type="checkbox"]',
                    ) as HTMLInputElement;
                    if (input) input.indeterminate = !!someSelected;
                  }}
                  onCheckedChange={(checked) => onSelectAll(!!checked)}
                />
              </th>
            )}
            <th className="text-left p-4">Name</th>
            <th className="text-left p-4">Email</th>
            <th className="text-left p-4">Role</th>
            <th className="text-left p-4">Venues</th>
            <th
              className="text-left p-4"
              title="Preview: not saved yet - shows the access each user would start with"
            >
              Feature access
            </th>
            <th className="text-left p-4">Status</th>
            <th className="w-16 text-center p-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users?.map((user) => (
            <tr key={user.id} className="border-b">
              {onSelectUser && (
                <td className="p-4">
                  <Checkbox
                    checked={selectedUsers?.has(user.id) || false}
                    onCheckedChange={(checked) =>
                      onSelectUser(user.id, !!checked)
                    }
                  />
                </td>
              )}
              <td className="p-4">{user.name || "N/A"}</td>
              <td className="p-4">{user.email || "N/A"}</td>
              <td className="p-4">{user.roles?.name || "N/A"}</td>
              <td className="p-4">{getVenueDisplay(user)}</td>
              {renderFeatureCell(user)}
              <td className="p-4">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    user.statuses?.name === "active"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400"
                  }`}
                >
                  {user.statuses?.name || "Unknown"}
                </span>
              </td>
              <td className="p-4">
                <div className="flex justify-center">
                  {lockedReason?.(user) ? (
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground"
                      title={lockedReason(user) ?? undefined}
                      aria-label={lockedReason(user) ?? undefined}
                    >
                      <Lock className="h-4 w-4" />
                    </span>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canOpenUser(user) && (
                          <DropdownMenuItem onClick={() => openUser(user)}>
                            {user.role_id === ROLES.BOOKING_STAFF ? (
                              <>
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                Edit permissions
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4 mr-2" />
                                View details
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                        {onEditUser && (
                          <DropdownMenuItem onClick={() => onEditUser(user)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit user
                          </DropdownMenuItem>
                        )}
                        {user.statuses?.name === "active" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDeleteUser(user)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {deleteLabel}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
